import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { AutomationReportViewModel } from '../../../../../viewmodels/AppViewModels';
import { GroupEmailTableAutomationRow } from '../GroupEmailTableAutomationRow';
import { styleSheet } from './styles';

type onActionFunc = (item: AutomationReportViewModel, e?: React.MouseEvent<HTMLElement>) => void;

interface IProps {
	items: Api.AutomationReportViewModel[];
	onCancelClick?: onActionFunc;
	onCancelStepClick?: onActionFunc;
	onManageClick?: onActionFunc;
	onSubjectClick?: onActionFunc;
	preventAutomationFunctionality?: boolean;
}

enum AutomationReportingGroup {
	PastDue = 'PastDue',
	Today = 'Today',
	Tomorrow = 'Tomorrow',
	DayAfterTomorrow = 'DayAfterTomorrow',
	EndOfWeek = 'EndOfWeek',
	EndOfMonth = 'EndOfMonth',
	More = 'More',
}

export const GroupedAutomations = ({
	items,
	onCancelClick,
	onCancelStepClick,
	onManageClick,
	onSubjectClick,
	preventAutomationFunctionality = false,
}: IProps) => {
	const format = 'MM/DD/YYYY';
	const today = moment().format(format);
	const tomorrow = moment().add(1, 'day').format(format);
	const dayAfterTomorrow = moment().add(2, 'day');
	const endOfWeek = moment().endOf('week');
	const endOfMonth = moment().endOf('month');
	const grouping: Record<AutomationReportingGroup, AutomationReportViewModel[]> = {
		[AutomationReportingGroup.PastDue]: [],
		[AutomationReportingGroup.Today]: [],
		[AutomationReportingGroup.Tomorrow]: [],
		[AutomationReportingGroup.DayAfterTomorrow]: [],
		[AutomationReportingGroup.EndOfWeek]: [],
		[AutomationReportingGroup.EndOfMonth]: [],
		[AutomationReportingGroup.More]: [],
	};
	items.forEach(item => {
		const dueDate = moment(item.dueDate);
		const dueDateFormatted = dueDate.format(format);
		if (dueDate.isBefore(today)) {
			grouping[AutomationReportingGroup.PastDue].push(item);
		} else if (dueDateFormatted === today) {
			grouping[AutomationReportingGroup.Today].push(item);
		} else if (dueDateFormatted === tomorrow) {
			grouping[AutomationReportingGroup.Tomorrow].push(item);
		} else if (dueDateFormatted === dayAfterTomorrow.format(format)) {
			grouping[AutomationReportingGroup.DayAfterTomorrow].push(item);
		} else if (dueDate.isBefore(endOfWeek)) {
			grouping[AutomationReportingGroup.EndOfWeek].push(item);
		} else if (dueDate.isBefore(endOfMonth)) {
			grouping[AutomationReportingGroup.EndOfMonth].push(item);
		} else {
			grouping[AutomationReportingGroup.More].push(item);
		}
	});

	const renderItem = (item: AutomationReportViewModel, index: number, group: AutomationReportingGroup) => {
		return (
			<GroupEmailTableAutomationRow
				className={css(styleSheet.automationRow)}
				key={item.id}
				onCancelAutomationClick={e => onCancelClick?.(item, e)}
				onCancelAutomationStepClick={e => onCancelStepClick?.(item, e)}
				onManageAutomationClick={e => onManageClick?.(item, e)}
				onSubjectClicked={e => onSubjectClick?.(item, e)}
				preventAutomationFunctionality={preventAutomationFunctionality}
				viewModel={item}
				isLastRow={index === grouping[group].length - 1}
			/>
		);
	};

	return (
		<>
			{grouping[AutomationReportingGroup.PastDue].length > 0 ? (
				<>
					<div className={css(styleSheet.automationGroupHeader)}>Past Due</div>
					{grouping[AutomationReportingGroup.PastDue].map((item, index) =>
						renderItem(item, index, AutomationReportingGroup.PastDue)
					)}
				</>
			) : null}
			{grouping[AutomationReportingGroup.Today].length > 0 ? (
				<>
					<div className={css(styleSheet.automationGroupHeader)}>Sending today</div>
					{grouping[AutomationReportingGroup.Today].map((item, index) =>
						renderItem(item, index, AutomationReportingGroup.Today)
					)}
				</>
			) : null}
			{grouping[AutomationReportingGroup.Tomorrow].length > 0 ? (
				<>
					<div className={css(styleSheet.automationGroupHeader)}>Sending tomorrow</div>
					{grouping[AutomationReportingGroup.Tomorrow].map((item, index) =>
						renderItem(item, index, AutomationReportingGroup.Tomorrow)
					)}
				</>
			) : null}
			{grouping[AutomationReportingGroup.DayAfterTomorrow].length > 0 ? (
				<>
					<div className={css(styleSheet.automationGroupHeader)}>
						Sending {moment(dayAfterTomorrow).format('dddd')} {dayAfterTomorrow.format(format)}
					</div>
					{grouping[AutomationReportingGroup.DayAfterTomorrow].map((item, index) =>
						renderItem(item, index, AutomationReportingGroup.DayAfterTomorrow)
					)}
				</>
			) : null}
			{grouping[AutomationReportingGroup.EndOfWeek].length > 0 ? (
				<>
					<div className={css(styleSheet.automationGroupHeader)}>Sending this week</div>
					{grouping[AutomationReportingGroup.EndOfWeek].map((item, index) =>
						renderItem(item, index, AutomationReportingGroup.EndOfWeek)
					)}
				</>
			) : null}
			{grouping[AutomationReportingGroup.EndOfMonth].length > 0 ? (
				<>
					<div className={css(styleSheet.automationGroupHeader)}>Sending this month</div>
					{grouping[AutomationReportingGroup.EndOfMonth].map((item, index) =>
						renderItem(item, index, AutomationReportingGroup.EndOfMonth)
					)}
				</>
			) : null}
			{grouping[AutomationReportingGroup.More].length > 0 ? (
				<>
					<div className={css(styleSheet.automationGroupHeader)}>More</div>
					{grouping[AutomationReportingGroup.More].map((item, index) =>
						renderItem(item, index, AutomationReportingGroup.More)
					)}
				</>
			) : null}
		</>
	);
};
